// Shared React hooks for admin pages and the shell.
//   useDebounce · useDebouncedCallback · useLocalStorage · useMediaQuery · useIsMobile
//   useHotkeys (combos "mod+k" and sequences "g o") · useUnsavedChanges · confirmNavigation
//   usePageTitle · useStoreEvents (SSE → react-query invalidation) · useLatest · useInterval
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { getStoreId, withStore } from './api'

// ─── small utilities ────────────────────────────────────────────────────────────────────

/** A ref that always holds the latest value (for stable callbacks that read fresh props). */
export function useLatest<T>(value: T) {
  const ref = useRef(value)
  ref.current = value
  return ref
}

/** Debounced copy of a value: `const q = useDebounce(search, 250)`. */
export function useDebounce<T>(value: T, delay = 250): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

/** Stable debounced function; `.flush()` runs a pending call now, `.cancel()` drops it. */
export function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, delay = 250) {
  const fnRef = useLatest(fn)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<A | null>(null)
  const cb = useMemo(() => {
    const run = (...args: A) => {
      pending.current = args
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => { timer.current = null; const a = pending.current; pending.current = null; if (a) fnRef.current(...a) }, delay)
    }
    return Object.assign(run, {
      cancel: () => { if (timer.current) clearTimeout(timer.current); timer.current = null; pending.current = null },
      flush: () => { if (timer.current) clearTimeout(timer.current); timer.current = null; const a = pending.current; pending.current = null; if (a) fnRef.current(...a) },
    })
  }, [delay, fnRef])
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  return cb
}

/** setInterval that respects the latest callback; pass null to pause. */
export function useInterval(fn: () => void, ms: number | null) {
  const ref = useLatest(fn)
  useEffect(() => {
    if (ms === null) return
    const t = setInterval(() => ref.current(), ms)
    return () => clearInterval(t)
  }, [ms, ref])
}

// ─── localStorage ───────────────────────────────────────────────────────────────────────

const LS_EVENT = 'local-storage-change'

/**
 * JSON state persisted in localStorage, synced across components and tabs. Storage can be
 * blocked (private mode) — then it behaves like useState. `validate` guards against stale or
 * tampered shapes (never trust what's in storage). A null key = plain state (nothing persisted).
 */
export function useLocalStorage<T>(key: string | null, initial: T, validate?: (v: unknown) => v is T): [T, (v: T | ((prev: T) => T)) => void] {
  const read = useCallback((): T => {
    if (key === null) return initial
    try {
      const raw = localStorage.getItem(key)
      if (raw === null) return initial
      const parsed = JSON.parse(raw) as unknown
      if (validate && !validate(parsed)) return initial
      return parsed as T
    } catch { return initial }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  const [value, setValue] = useState<T>(read)
  const valueRef = useLatest(value)

  useEffect(() => { setValue(read()) }, [read])
  useEffect(() => {
    if (key === null) return
    const onStorage = (e: StorageEvent) => { if (e.key === key) setValue(read()) }
    const onLocal = (e: Event) => { if ((e as CustomEvent).detail === key) setValue(read()) }
    window.addEventListener('storage', onStorage)
    window.addEventListener(LS_EVENT, onLocal)
    return () => { window.removeEventListener('storage', onStorage); window.removeEventListener(LS_EVENT, onLocal) }
  }, [key, read])

  const set = useCallback((v: T | ((prev: T) => T)) => {
    const next = typeof v === 'function' ? (v as (p: T) => T)(valueRef.current) : v
    setValue(next)
    if (key === null) return
    try { localStorage.setItem(key, JSON.stringify(next)); window.dispatchEvent(new CustomEvent(LS_EVENT, { detail: key })) } catch { /* blocked */ }
  }, [key, valueRef])
  return [value, set]
}

// ─── media queries ──────────────────────────────────────────────────────────────────────

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((cb: () => void) => {
    const mq = window.matchMedia(query)
    mq.addEventListener('change', cb)
    return () => mq.removeEventListener('change', cb)
  }, [query])
  return useSyncExternalStore(subscribe, () => window.matchMedia(query).matches, () => false)
}
/** True below the `lg` breakpoint (1024 px) — the shell switches the sidebar to a drawer there. */
export const useIsMobile = () => useMediaQuery('(max-width: 1023.98px)')

// ─── hotkeys ────────────────────────────────────────────────────────────────────────────

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent)

/** Return `false` to pass the key on to the next binding for the same combo (the default is still prevented). */
type HotkeyHandler = (e: KeyboardEvent) => void | boolean
export interface HotkeyOptions {
  /** Fire even when focus is in an input/textarea/contenteditable. Default: only for mod/ctrl/alt combos. */
  inInputs?: boolean
  /** Fire while a dialog, popover, menu or listbox is open. Default: only for mod/ctrl/alt combos. */
  inModals?: boolean
  /** Call preventDefault (default true). */
  preventDefault?: boolean
  enabled?: boolean
}
interface Binding { steps: string[]; handler: { current: HotkeyHandler }; opts: HotkeyOptions; hasModifier: boolean }

const MOD_ORDER = ['mod', 'ctrl', 'meta', 'alt', 'shift'] as const
const KEY_ALIASES: Record<string, string> = { esc: 'escape', return: 'enter', del: 'delete', up: 'arrowup', down: 'arrowdown', left: 'arrowleft', right: 'arrowright', space: ' ', plus: '+' }

/** Normalise a combo string ("Mod+Shift+P", "?", "g") to canonical form. Exported for tests. */
export function normalizeCombo(combo: string): string {
  const parts = combo.trim().toLowerCase().split('+')
  // "shift++" / "+" handling: an empty trailing part means the key itself is "+".
  let key = parts.pop() ?? ''
  if (key === '' && parts.length) { parts.pop(); key = '+' }
  key = KEY_ALIASES[key] ?? key
  const mods = new Set(parts.map(p => (p === 'cmd' || p === 'command' ? (isMac ? 'mod' : 'meta') : p === 'control' ? 'ctrl' : p === 'option' ? 'alt' : p)))
  if (!isMac && mods.has('ctrl')) { mods.delete('ctrl'); mods.add('mod') }
  if (isMac && mods.has('meta')) { mods.delete('meta'); mods.add('mod') }
  // Shift is implied by printable symbols ("?", "!"): drop it so "shift+?" == "?".
  if (key.length === 1 && !/[a-z0-9]/.test(key)) mods.delete('shift')
  return [...MOD_ORDER.filter(m => mods.has(m)), key].join('+')
}

/** Canonical combo for a keyboard event (same format as normalizeCombo). Exported for tests. */
export function eventCombo(e: Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>): string {
  let key = (e.key || '').toLowerCase()
  // Option on macOS turns letters into symbols (⌥K = "˚"): use the physical key instead.
  if (e.altKey && /^Key[A-Z]$/.test(e.code)) key = e.code.slice(3).toLowerCase()
  if (e.altKey && /^Digit\d$/.test(e.code)) key = e.code.slice(5)
  key = KEY_ALIASES[key] ?? key
  const mods: string[] = []
  if (isMac ? e.metaKey : e.ctrlKey) mods.push('mod')
  if (isMac && e.ctrlKey) mods.push('ctrl')
  if (!isMac && e.metaKey) mods.push('meta')
  if (e.altKey) mods.push('alt')
  if (e.shiftKey && !(key.length === 1 && !/[a-z0-9]/.test(key))) mods.push('shift')
  return [...MOD_ORDER.filter(m => mods.includes(m)), key].join('+')
}

export function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  if (!el || !el.tagName) return false
  if (el.isContentEditable) return true
  const tag = el.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type)
  }
  return !!el.closest?.('[data-hotkeys-ignore]')
}
const OVERLAY_SELECTOR = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]'
/** Any floating layer is open (dialog, sheet, popover, menu, select list) — plain-key shortcuts pause. */
export function isOverlayOpen(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector(OVERLAY_SELECTOR)
}
/**
 * A MODAL layer is open (Dialog, Sheet, confirm, ⌘K) — not a popover, date picker or menu, which
 * Radix also gives role="dialog" but renders inside a popper wrapper and doesn't make modal.
 */
export function isModalOpen(): boolean {
  if (typeof document === 'undefined') return false
  for (const el of document.querySelectorAll('[aria-modal="true"], [role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
    if (el.getAttribute('data-state') === 'closed' || el.getAttribute('aria-modal') === 'false') continue
    if (el.closest('[data-radix-popper-content-wrapper]')) continue
    return true
  }
  return false
}

const bindings = new Set<Binding>()
let buffer: Array<{ combo: string; at: number }> = []
let listening = false
const SEQUENCE_TIMEOUT = 1000

function onKeyDown(e: KeyboardEvent) {
  if (e.isComposing || e.key === 'Dead' || ['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return
  const combo = eventCombo(e)
  const now = Date.now()
  buffer = buffer.filter(b => now - b.at < SEQUENCE_TIMEOUT).slice(-3)
  buffer.push({ combo, at: now })
  const editable = isEditableTarget(e.target)
  const overlay = isOverlayOpen()
  // Longest sequences win ("g o" over a lone "o"); among equals the most recently mounted binding
  // wins (a dialog's ⌘S over the page's), and a handler returning false passes the key on.
  const sorted = [...bindings].reverse().sort((a, b) => b.steps.length - a.steps.length)
  let matched = false
  for (const b of sorted) {
    if (b.opts.enabled === false) continue
    if (editable && !(b.opts.inInputs ?? b.hasModifier)) continue
    if (overlay && !(b.opts.inModals ?? b.hasModifier)) continue
    if (b.steps.length > buffer.length) continue
    const tail = buffer.slice(-b.steps.length)
    if (!tail.every((t, i) => t.combo === b.steps[i])) continue
    if (e.repeat && b.steps.length > 1) continue
    if (b.opts.preventDefault !== false) e.preventDefault()
    matched = true
    if (b.handler.current(e) === false) continue
    buffer = []
    return
  }
  if (matched) { buffer = []; return }
  // A plain key typed into an input must not start a sequence later.
  if (editable) buffer = []
}

/**
 * Register keyboard shortcuts while mounted.
 * `useHotkeys({ 'mod+k': open, 'g o': () => nav('/orders'), '?': showHelp })`
 * Combos: mod (⌘ on macOS, Ctrl elsewhere), ctrl, alt, shift + key. Sequences: steps separated
 * by a space, typed within 1 s. Plain keys are ignored while typing in a field or when a modal is
 * open, unless `inInputs` / `inModals` is set; modifier combos fire everywhere.
 */
export function useHotkeys(map: Record<string, HotkeyHandler>, opts: HotkeyOptions = {}, deps: unknown[] = []) {
  const handlers = useRef(map)
  handlers.current = map
  const keys = Object.keys(map).join('|')
  useEffect(() => {
    if (opts.enabled === false) return
    const mine: Binding[] = Object.keys(handlers.current).map(k => {
      const steps = k.trim().split(/\s+/).map(normalizeCombo)
      const handler = { get current() { return handlers.current[k] ?? (() => {}) } } as { current: HotkeyHandler }
      return { steps, handler, opts, hasModifier: steps.some(s => /(^|\+)(mod|ctrl|meta|alt)\+/.test(s)) }
    })
    mine.forEach(b => bindings.add(b))
    if (!listening) { window.addEventListener('keydown', onKeyDown); listening = true }
    return () => {
      mine.forEach(b => bindings.delete(b))
      if (!bindings.size && listening) { window.removeEventListener('keydown', onKeyDown); listening = false }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys, opts.enabled, opts.inInputs, opts.inModals, opts.preventDefault, ...deps])
}

/** Human label for a combo: "mod+k" → "⌘K" on macOS, "Ctrl+K" elsewhere. */
export function comboLabel(combo: string): string[] {
  return combo.trim().split(/\s+/).map(step => normalizeCombo(step).split('+').map(p => {
    if (p === 'mod') return isMac ? '⌘' : 'Ctrl'
    if (p === 'ctrl') return isMac ? '⌃' : 'Ctrl'
    if (p === 'alt') return isMac ? '⌥' : 'Alt'
    if (p === 'shift') return isMac ? '⇧' : 'Shift'
    if (p === 'meta') return 'Win'
    if (p === 'escape') return 'Esc'
    if (p === 'enter') return '↵'
    if (p === 'arrowup') return '↑'
    if (p === 'arrowdown') return '↓'
    if (p === ' ') return 'Space'
    return p.length === 1 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)
  }).join(isMac ? '' : '+'))
}

// ─── unsaved changes ────────────────────────────────────────────────────────────────────

type LeavePrompt = (message: string) => Promise<boolean>
let leavePrompt: LeavePrompt = async (m) => window.confirm(m)
/** The UI kit's ConfirmHost installs a nicer dialog here; tests/kit can too. */
export function setLeavePrompt(fn: LeavePrompt | null) { leavePrompt = fn ?? (async (m) => window.confirm(m)) }

const guards = new Map<symbol, string>()
let bypass = false
const DEFAULT_LEAVE = 'You have unsaved changes. Leave this page and discard them?'

/** Whether any mounted form has unsaved changes. */
export function hasUnsavedChanges() { return guards.size > 0 && !bypass }

/**
 * Ask before a programmatic navigation when a form is dirty. Resolves true when it's fine to go.
 * The shell's sidebar, ⌘K palette and shortcuts call this; pages can too before navigate().
 */
export async function confirmNavigation(): Promise<boolean> {
  if (!hasUnsavedChanges()) return true
  const msg = [...guards.values()][guards.size - 1] || DEFAULT_LEAVE
  const ok = await leavePrompt(msg)
  if (ok) { bypass = true; setTimeout(() => { bypass = false }, 1500) }
  return ok
}

function onBeforeUnload(e: BeforeUnloadEvent) {
  if (!hasUnsavedChanges()) return
  e.preventDefault()
  e.returnValue = ''
}

function onDocumentClick(e: MouseEvent) {
  if (!hasUnsavedChanges() || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null
  if (!a || a.hasAttribute('download') || (a.target && a.target !== '_self') || a.dataset.allowUnsaved !== undefined) return
  let url: URL
  try { url = new URL(a.href, location.href) } catch { return }
  if (url.origin !== location.origin) return          // external: beforeunload covers it
  if (url.pathname === location.pathname && url.search === location.search) return  // hash/same page
  e.preventDefault()
  e.stopPropagation()
  void confirmNavigation().then(ok => { if (ok) a.click() })
}

// Back/forward: the Navigation API lets us cancel same-document traversals (Chromium). Where it
// is missing, links, the palette and beforeunload are still guarded.
type NavEvent = Event & { navigationType?: string; cancelable: boolean; destination?: { key?: string; sameDocument?: boolean } }
function onNavigate(e: Event) {
  const ev = e as NavEvent
  if (!hasUnsavedChanges() || ev.navigationType !== 'traverse' || !ev.cancelable || ev.destination?.sameDocument === false) return
  const key = ev.destination?.key
  ev.preventDefault()
  void confirmNavigation().then(ok => {
    const nav = (window as any).navigation
    if (ok && key && nav?.traverseTo) nav.traverseTo(key)
  })
}

function installGuardListeners() {
  window.addEventListener('beforeunload', onBeforeUnload)
  document.addEventListener('click', onDocumentClick, true)
  ;(window as any).navigation?.addEventListener?.('navigate', onNavigate)
}
function removeGuardListeners() {
  window.removeEventListener('beforeunload', onBeforeUnload)
  document.removeEventListener('click', onDocumentClick, true)
  ;(window as any).navigation?.removeEventListener?.('navigate', onNavigate)
}

/**
 * Warn before leaving while `dirty`: tab close/reload, in-app links, back/forward (where the
 * browser allows cancelling it), and the shell's own navigation. StickySaveBar calls this for you.
 */
export function useUnsavedChanges(dirty: boolean, message = DEFAULT_LEAVE) {
  useEffect(() => {
    if (!dirty) return
    const id = Symbol('unsaved')
    guards.set(id, message)
    if (guards.size === 1) installGuardListeners()
    return () => {
      guards.delete(id)
      if (!guards.size) removeGuardListeners()
    }
  }, [dirty, message])
}

// ─── document title ─────────────────────────────────────────────────────────────────────

const titleState: { storeName: string | null; routeTitle: string | null; override: { path: string; title: string } | null; suffix: string } = {
  storeName: null, routeTitle: null, override: null, suffix: 'Admin',
}
const titleListeners = new Set<(pageTitle: string | null, path: string) => void>()
function applyTitle() {
  if (typeof document === 'undefined') return
  const path = location.pathname
  const page = titleState.override && titleState.override.path === path ? titleState.override.title : titleState.routeTitle
  document.title = [page, titleState.storeName, titleState.suffix].filter(Boolean).join(' · ')
  titleListeners.forEach(fn => fn(page, path))
}
/** The current page's title (override or route title). */
export function getPageTitle(): string | null {
  const path = typeof location !== 'undefined' ? location.pathname : ''
  return titleState.override && titleState.override.path === path ? titleState.override.title : titleState.routeTitle
}
/** Shell-only: observe page title changes (recent pages in ⌘K use it). */
export function onTitleChange(fn: (pageTitle: string | null, path: string) => void) { titleListeners.add(fn); return () => { titleListeners.delete(fn) } }
/** Shell-only: set the route's default title and the current store name. */
export function setTitleContext(next: { storeName?: string | null; routeTitle?: string | null; suffix?: string }) {
  if (next.storeName !== undefined) titleState.storeName = next.storeName
  if (next.routeTitle !== undefined) titleState.routeTitle = next.routeTitle
  if (next.suffix !== undefined) titleState.suffix = next.suffix
  applyTitle()
}

/**
 * Page title: `usePageTitle(order ? order.name : null)` → "#1001 · My Store · Admin". Without it
 * the shell uses the route title from app/routes.tsx. Pass null while loading.
 */
export function usePageTitle(title: string | null | undefined) {
  useEffect(() => {
    if (!title) return
    const path = location.pathname
    titleState.override = { path, title }
    applyTitle()
    return () => {
      if (titleState.override?.path === path && titleState.override.title === title) titleState.override = null
      applyTitle()
    }
  }, [title])
}

// ─── live store events (SSE) ────────────────────────────────────────────────────────────

export interface StoreEvent<T = any> { type: string; data: T; receivedAt: number }
type StoreEventListener = (e: StoreEvent) => void

/**
 * Event type → react-query key prefixes to invalidate. Convention for every module: list and
 * detail queries start with the plural entity name, e.g. ['orders', …], ['products', …].
 */
export const EVENT_INVALIDATIONS: Record<string, string[][]> = {
  order: [['orders'], ['home'], ['analytics'], ['notifications']],
  payment: [['orders'], ['payments'], ['home']],
  checkout: [['checkouts'], ['home']],
  product: [['products'], ['inventory'], ['collections']],
  inventory: [['inventory'], ['products']],
  collection: [['collections']],
  customer: [['customers']],
  review: [['reviews']],
  notification: [['notifications']],
  import: [['imports'], ['products']],
  domain: [['domains']],
  store: [['store']],
  discount: [['discounts']],
  /** Inbox (Phase 2): new messages / status changes refresh the inbox lists, threads and the nav badge. */
  conversation: [['inbox']],
}

/** Known named SSE events, in case the server uses `event: <type>` lines instead of plain messages. */
const NAMED_EVENTS = [
  'order.created', 'order.updated', 'order.paid', 'order.fulfilled', 'order.cancelled', 'order.refunded',
  'payment.succeeded', 'payment.failed', 'payment.mismatch', 'checkout.started', 'checkout.completed', 'checkout.recovered',
  'product.created', 'product.updated', 'product.deleted', 'inventory.updated', 'collection.updated',
  'customer.created', 'customer.updated', 'review.created', 'notification.created',
  'import.progress', 'import.completed', 'domain.updated', 'store.updated', 'discount.updated',
]

class EventHub {
  private es: EventSource | null = null
  private storeId: string | null = null
  private listeners = new Set<StoreEventListener>()
  private clients = new Set<QueryClient>()
  private retry = 0
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null
  private unavailableUntil = 0
  status: 'idle' | 'connecting' | 'open' | 'unavailable' = 'idle'

  subscribe(listener: StoreEventListener | null, qc: QueryClient | null) {
    if (listener) this.listeners.add(listener)
    if (qc) this.clients.add(qc)
    this.ensure()
    return () => {
      if (listener) this.listeners.delete(listener)
      if (qc) this.clients.delete(qc)
      if (!this.listeners.size && !this.clients.size) this.close()
    }
  }
  private ensure() {
    const sid = getStoreId()
    if (!sid || typeof EventSource === 'undefined') return
    if (this.es && this.storeId === sid) return
    if (Date.now() < this.unavailableUntil && this.storeId === sid) return
    this.open(sid)
  }
  /** Re-point the stream after a store switch. */
  refresh() { if (this.listeners.size || this.clients.size) { this.close(false); this.ensure() } }

  private open(sid: string) {
    this.close(false)
    this.storeId = sid
    this.status = 'connecting'
    const es = new EventSource(withStore('/api/admin/events'))
    this.es = es
    es.onopen = () => { this.retry = 0; this.status = 'open' }
    es.onmessage = (m) => this.dispatch('message', m.data)
    for (const t of NAMED_EVENTS) es.addEventListener(t, (m) => this.dispatch(t, (m as MessageEvent).data))
    es.onerror = () => {
      if (es.readyState !== EventSource.CLOSED) return   // the browser is already reconnecting
      this.es = null
      void this.diagnoseAndRetry(sid)
    }
    if (!this.visibilityInstalled) this.installVisibility()
  }
  private visibilityInstalled = false
  private installVisibility() {
    this.visibilityInstalled = true
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Free the connection for background tabs after a minute.
        this.hiddenTimer = setTimeout(() => { if (this.es) { this.close(false); this.status = 'idle' } }, 60_000)
      } else {
        if (this.hiddenTimer) clearTimeout(this.hiddenTimer)
        if (!this.es && (this.listeners.size || this.clients.size) && this.status !== 'unavailable') {
          this.ensure()
          this.invalidateAll()   // events may have been missed while closed
        }
      }
    })
  }
  private async diagnoseAndRetry(sid: string) {
    // EventSource hides the HTTP status. Probe once: 404 = the endpoint doesn't exist yet
    // (tolerated: stay quiet and look again in 5 minutes); 401/403 = signed out / no access.
    let status = 0
    try {
      const ctrl = new AbortController()
      const res = await fetch(withStore('/api/admin/events'), { headers: { 'X-Requested-With': 'fetch', Accept: 'text/event-stream' }, credentials: 'same-origin', signal: ctrl.signal })
      status = res.status
      ctrl.abort()
    } catch { status = 0 }
    if (sid !== getStoreId()) return
    if (status === 404 || status === 401 || status === 403) {
      this.status = 'unavailable'
      this.unavailableUntil = Date.now() + 5 * 60_000
      this.retryTimer = setTimeout(() => { this.unavailableUntil = 0; this.status = 'idle'; this.ensure() }, 5 * 60_000)
      return
    }
    const delay = Math.min(30_000, 1000 * 2 ** this.retry++) + Math.random() * 500
    this.status = 'connecting'
    this.retryTimer = setTimeout(() => { if (sid === getStoreId()) this.open(sid) }, delay)
  }
  private dispatch(name: string, raw: string) {
    let parsed: any = null
    try { parsed = JSON.parse(raw) } catch { return }
    if (!parsed || typeof parsed !== 'object') return
    const type: string = typeof parsed.type === 'string' ? parsed.type : name
    if (!type || type === 'message' || type === 'ping' || type === 'ready' || type === 'heartbeat') return
    const evt: StoreEvent = { type, data: 'data' in parsed && typeof parsed.type === 'string' ? parsed.data : parsed, receivedAt: Date.now() }
    const prefixes = EVENT_INVALIDATIONS[type.split('.')[0]]
    if (prefixes) for (const qc of this.clients) for (const key of prefixes) void qc.invalidateQueries({ queryKey: key })
    for (const l of [...this.listeners]) { try { l(evt) } catch (err) { console.error('[store events] listener failed', err) } }
  }
  private invalidateAll() {
    const all = new Set(Object.values(EVENT_INVALIDATIONS).flat().map(k => k[0]))
    for (const qc of this.clients) for (const k of all) void qc.invalidateQueries({ queryKey: [k] })
  }
  private close(reset = true) {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null }
    this.es?.close()
    this.es = null
    if (reset) { this.status = 'idle'; this.retry = 0 }
  }
}
const hub = new EventHub()
/** Shell-only: reconnect the SSE stream to the newly selected store. */
export const refreshStoreEvents = () => hub.refresh()

/**
 * Live updates for the current store over SSE (`GET /api/admin/events?storeId=`). One shared
 * EventSource serves every subscriber; it reconnects with backoff, closes in background tabs,
 * and tolerates the endpoint not existing yet (404). By default each event invalidates the
 * matching react-query keys (see EVENT_INVALIDATIONS); pass a handler to react yourself:
 *   useStoreEvents(e => { if (e.type === 'order.created') highlight(e.data.id) })
 * Server contract: `data: {"type":"order.created","data":{…}}` lines (or named `event:` lines).
 */
export function useStoreEvents(handler?: (e: StoreEvent) => void, opts: { enabled?: boolean; invalidate?: boolean } = {}) {
  const qc = useQueryClient()
  const handlerRef = useLatest(handler)
  const enabled = opts.enabled !== false
  const invalidate = opts.invalidate !== false
  const hasHandler = !!handler
  useEffect(() => {
    if (!enabled) return
    const listener: StoreEventListener | null = hasHandler ? (e) => handlerRef.current?.(e) : null
    return hub.subscribe(listener, invalidate ? qc : null)
  }, [enabled, invalidate, hasHandler, qc, handlerRef])
}
